import styled from 'styled-components';
import { PHONE_MEDIA_QUERY } from '../../../../common/responsive';
import { useEffect, useRef, useState } from 'react';
import Button from '../../../../components/Button';
import { useGiftForm } from '../../../../components/GiftFormContext';
import {
  selectTokenByNetworkAndAddress,
  selectTokenByNetworkAndSymbol,
} from '../../../../redux/slices/tokenSlice';
import { useSelector } from 'react-redux';
import WalletSwapIcon from '../../../../assets/icons/wallet-swap.svg?react';
import { fromTokenAmount, toTokenAmount } from '../../../../hooks/useSolana';
import { hapticsImpactLight } from '../../../../common/haptic';
import { useLazyCreateOrderQuery } from '../../../../redux/api/authorized/ultra';
import { BodyLarge } from '../../../../components/Texts';
import { formatTokenPrice } from '../../../../common/currency';
import { selectPrivyToken } from '../../../../redux/slices/auth';
import {
  EVENT_GIFT_FLOW_PRICE_BUTTON_CLICKED,
  EVENT_GIFT_FLOW_PRICE_CHANGED,
  EVENT_GIFT_FLOW_PRICE_SELECTION_COMPLETED,
  sendEvent,
} from '../../../../common/analytics';

const StyledInput = styled.input`
  height: 48px;
  background-color: red;
  display: inline-block;
  width: fit-content;
  flex: 0 0 auto;
  border-radius: 16px;
  text-align: center;
  border: none;
  margin-left: -14px;
  margin-right: -14px;
  text-align: center;
  font-size: clamp(20px, 9vw, 64px);
  font-weight: 700;
  background-color: transparent;

  color: ${({ theme }) => theme.text.title};
  outline: none;
  @media ${PHONE_MEDIA_QUERY} {
    font-size: clamp(14px, 9vw, 64px);
  }
`;

const InputContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  height: fit-content;
  position: relative;
  flex: 0 0 auto;
  max-width: 100%;
  padding-left: 14px;
  padding-right: 14px;
  overflow: hidden;
`;

const InsideInputContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  width: 100%;
  align-items: center;
  pointer-events: none;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  flex-wrap: nowrap;
  text-align: center;
  font-size: clamp(20px, 9vw, 64px);
  font-weight: 700;
  color: ${({ theme }) => theme.text.title};
  text-wrap: nowrap;
  &:focus {
    outline: none;
  }
  @media ${PHONE_MEDIA_QUERY} {
    font-size: clamp(14px, 9vw, 64px);
  }
`;

const AmountButton = styled.button`
  flex: 1;
  border-radius: 16px;
  border: 1px solid
    ${(props) =>
      props.$active
        ? `${props.theme.primary.primary}`
        : `${props.theme.border.primary}`};
  padding: 12px 12px;
  background-color: ${(props) =>
    props.$active
      ? `${props.theme.background.primary}`
      : `${props.theme.background.light}`};
  color: ${({ theme }) => theme.text.title};
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;

  @media (hover: hover) {
    &:hover:not(:disabled) {
      border-color: ${(props) =>
        props.$active
          ? `${props.theme.primary.primary}`
          : `${props.theme.text.disabled}`};
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const AmountButtonContainer = styled.div`
  display: flex;
  gap: 4px;
  width: 100%;
`;

const SwapAmountContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  cursor: pointer;
`;

const AmountsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  justify-content: center;
  align-items: center;
`;

const PaymentSelectorAndButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const AmountSelector = ({
  onContinue,
  buttonLabel = 'Continue',
  isModal,
  currentStep,
}) => {
  const {
    targetTokenAddress,
    network,
    usdAmount,
    setValue,
    setFromUsdcDirection,
    fromUsdcDirection,
    targetTokenAmount,
  } = useGiftForm();

  const targetToken = useSelector((state) =>
    selectTokenByNetworkAndAddress(state, network, targetTokenAddress),
  );

  const tokenSymbolRefElement = useRef(null);
  const [labelWidth, setLabelWidth] = useState(0);

  const [fullyRendered, setFullyRendered] = useState(false);
  const inputRef = useRef(null);
  const [loadingTokenAmount, setLoadingTokenAmount] = useState(false);

  const usdcToken = useSelector((state) =>
    selectTokenByNetworkAndSymbol(state, network, 'USDC'),
  );

  const privyToken = useSelector(selectPrivyToken);
  const isAuthenticated = !!privyToken;

  // Force USD direction for non-authenticated users
  useEffect(() => {
    if (!isAuthenticated && !fromUsdcDirection) {
      setFromUsdcDirection(true);
    }
  }, [isAuthenticated, fromUsdcDirection, setFromUsdcDirection]);

  const [swapQuote, setSwapQuote] = useState({
    fromToken: null,
    toToken: null,
    amount: null,
  });

  const [triggerCreateOrder] = useLazyCreateOrderQuery();

  useEffect(() => {
    if (tokenSymbolRefElement?.current) {
      setLabelWidth(tokenSymbolRefElement?.current?.offsetWidth || 0);
    }
  }, [fromUsdcDirection, targetToken?.symbol, targetTokenAmount, usdAmount]);

  useEffect(() => {
    // Skip quote for non-authenticated users
    if (!isAuthenticated) return;
    if (usdcToken && targetToken && !targetTokenAmount && fromUsdcDirection) {
      setSwapQuote({
        fromToken: usdcToken,
        toToken: targetToken,
        amount: usdAmount,
      });
    }
  }, [usdcToken, targetTokenAmount, targetToken, usdAmount, isAuthenticated]);

  useEffect(() => {
    // Skip quote for non-authenticated users
    if (!isAuthenticated) return;
    if (
      swapQuote.fromToken &&
      swapQuote.toToken &&
      swapQuote.amount &&
      parseFloat(swapQuote.amount) > 0
    ) {
      const fetchQuote = async () => {
        setSwapQuote({
          fromToken: null,
          toToken: null,
          amount: null,
        });
        setLoadingTokenAmount(true);

        // If both tokens are USDC, no conversion needed
        if (
          swapQuote.fromToken.address === usdcToken.address &&
          swapQuote.toToken.address === usdcToken.address
        ) {
          setValue('usdAmount', swapQuote.amount);
          setValue('targetTokenAmount', swapQuote.amount);
        } else {
          // Always convert when tokens are different
          const orderResult = await triggerCreateOrder({
            inputMint: swapQuote.fromToken.address,
            outputMint: swapQuote.toToken.address,
            amount: toTokenAmount(
              swapQuote.amount,
              swapQuote.fromToken.decimals,
            ),
          }).unwrap();
          if (orderResult?.outAmount) {
            const exactAmountNeeded = orderResult.outAmount;
            const formatedTokenAmount = fromTokenAmount(
              exactAmountNeeded,
              swapQuote.toToken?.decimals,
            );
            if (swapQuote.fromToken.address === usdcToken.address) {
              // Converting USDC → targetToken: set targetTokenAmount
              setValue('targetTokenAmount', formatedTokenAmount);
            } else {
              // Converting targetToken → USDC: set usdAmount
              setValue('usdAmount', formatedTokenAmount);
            }
          }
        }
        setLoadingTokenAmount(false);
      };
      fetchQuote();
    }
  }, [swapQuote, isAuthenticated]);

  useEffect(() => {
    if (!fullyRendered) return;
    if (!inputRef.current) return;
    inputRef.current.focus();
  }, [fullyRendered, inputRef.current]);

  return (
    <>
      <AmountsContainer>
        <InputContainer>
          {fromUsdcDirection && (
            <InsideInputContainer>
              <span>$</span>
            </InsideInputContainer>
          )}
          <StyledInput
            key={tokenSymbolRefElement.current?.offsetWidth}
            style={{
              maxWidth: `${labelWidth + 42}px`,
            }}
            autoFocus={true}
            ref={inputRef}
            value={fromUsdcDirection ? usdAmount : targetTokenAmount}
            id='amount-input'
            inputMode='numeric'
            onChange={(e) => {
              sendEvent(EVENT_GIFT_FLOW_PRICE_CHANGED, {
                price: e.target.value,
              });
              if (fromUsdcDirection) {
                setValue('usdAmount', e.target.value);
                // Only set swap quote for authenticated users
                if (isAuthenticated) {
                  setSwapQuote({
                    fromToken: usdcToken,
                    toToken: targetToken,
                    amount: e.target.value,
                  });
                }
              } else {
                setValue('targetTokenAmount', e.target.value);
                // Only set swap quote for authenticated users
                if (isAuthenticated) {
                  setSwapQuote({
                    fromToken: targetToken,
                    toToken: usdcToken,
                    amount: e.target.value,
                  });
                }
              }
            }}
          />
          {!fromUsdcDirection && (
            <InsideInputContainer>
              <span>{targetToken?.symbol}</span>
            </InsideInputContainer>
          )}
        </InputContainer>
        <InsideInputContainer style={{ position: 'absolute' }}>
          <span style={{ color: 'transparent' }} ref={tokenSymbolRefElement}>
            {fromUsdcDirection ? usdAmount : targetTokenAmount}
          </span>
        </InsideInputContainer>
        {targetToken?.symbol !== 'USDC' && isAuthenticated && (
          <SwapAmountContainer
            onClick={() => setFromUsdcDirection(!fromUsdcDirection)}
          >
            <BodyLarge>
              {fromUsdcDirection
                ? `${formatTokenPrice(targetTokenAmount, targetToken?.decimals)} ${targetToken?.symbol}`
                : `$${usdAmount}`}
            </BodyLarge>
            <WalletSwapIcon width={18} height={18} />
          </SwapAmountContainer>
        )}
      </AmountsContainer>
      <AmountButtonContainer>
        <AmountButton
          onClick={() => {
            setValue('usdAmount', 30);
            hapticsImpactLight();
            sendEvent(EVENT_GIFT_FLOW_PRICE_BUTTON_CLICKED, {
              price: 30,
            });
            // Only set swap quote for authenticated users
            if (isAuthenticated) {
              setSwapQuote({
                fromToken: usdcToken,
                toToken: targetToken,
                amount: 30,
              });
            }
          }}
        >
          $30
        </AmountButton>
        <AmountButton
          onClick={() => {
            setValue('usdAmount', 40);
            hapticsImpactLight();
            sendEvent(EVENT_GIFT_FLOW_PRICE_BUTTON_CLICKED, {
              price: 40,
            });
            // Only set swap quote for authenticated users
            if (isAuthenticated) {
              setSwapQuote({
                fromToken: usdcToken,
                toToken: targetToken,
                amount: 40,
              });
            }
          }}
        >
          $40
        </AmountButton>
        <AmountButton
          onClick={() => {
            setValue('usdAmount', 50);
            hapticsImpactLight();
            sendEvent(EVENT_GIFT_FLOW_PRICE_BUTTON_CLICKED, {
              price: 50,
            });
            // Only set swap quote for authenticated users
            if (isAuthenticated) {
              setSwapQuote({
                fromToken: usdcToken,
                toToken: targetToken,
                amount: 50,
              });
            }
          }}
        >
          $50
        </AmountButton>
        <AmountButton
          onClick={() => {
            setValue('usdAmount', 100);
            hapticsImpactLight();
            sendEvent(EVENT_GIFT_FLOW_PRICE_BUTTON_CLICKED, {
              price: 100,
            });
            // Only set swap quote for authenticated users
            if (isAuthenticated) {
              setSwapQuote({
                fromToken: usdcToken,
                toToken: targetToken,
                amount: 100,
              });
            }
          }}
        >
          $100
        </AmountButton>
      </AmountButtonContainer>
      <PaymentSelectorAndButtonContainer>
        <Button
          fullWidth
          loading={loadingTokenAmount}
          onClick={() => {
            onContinue();
            sendEvent(EVENT_GIFT_FLOW_PRICE_SELECTION_COMPLETED);
          }}
        >
          {buttonLabel}
        </Button>
      </PaymentSelectorAndButtonContainer>
    </>
  );
};

export default AmountSelector;
